import { useCallback, useEffect, useRef } from "react";
import { extractMessageFromError } from "../helper_functions/utils";

export function useLineItemRequests(setErrorMessage: (message: string) => void): {
  request: <T>(load: (signal: AbortSignal) => Promise<T>, apply: (response: T) => void) => Promise<void>;
  cancel: () => void;
} {
  const controllers = useRef(new Set<AbortController>());
  const cancel = useCallback((): void => {
    controllers.current.forEach((controller) => { controller.abort(); });
    controllers.current.clear();
  }, []);
  useEffect(() => cancel, [cancel]);

  const request = useCallback(async <T,>(
    load: (signal: AbortSignal) => Promise<T>,
    apply: (response: T) => void,
  ): Promise<void> => {
    const controller = new AbortController();
    controllers.current.add(controller);
    try {
      const response = await load(controller.signal);
      if (!controller.signal.aborted) apply(response);
    } catch (error: unknown) {
      if (!controller.signal.aborted) setErrorMessage(extractMessageFromError(error));
    } finally {
      controllers.current.delete(controller);
    }
  }, [setErrorMessage]);

  return { request, cancel };
}
